// application/ports/versionErrors.js
//
// Two error classes in one file, against the usual one-per-file rule: they are the two failure modes
// of appending a version, a caller distinguishing them imports both, and splitting the pair across
// two files to satisfy a file-shape rule would make them harder to read, not easier.
/* eslint-disable max-classes-per-file */

/**
 * The two ways appending a version to an effective-dated series can fail.
 *
 * SHARED BY EVERY VERSIONED AGGREGATE IN THIS CONTEXT rather than owned by one of them. Funding
 * sources defined these first and charge masters need exactly the same pair — not something similar,
 * the same: the collision is detectable only inside the transaction on both, and the retry policy
 * that reads them lives in the use case on both. A second, parallel definition would let the two
 * drift into meaning slightly different things, and a caller handling one would silently fail to
 * handle the other.
 *
 * WHY THEY ARE PART OF THE PORT rather than raised as strings by the adapter. A caller has to
 * distinguish "try again" from "this date is not allowed", and Firestore's own error for the losing
 * write is `permission-denied` — indistinguishable from "you are not a manager at this clinic". The
 * adapter has to narrow that into something a retry loop can act on safely.
 */

/**
 * Another writer took the version number this save was computed against.
 *
 * Recoverable, and the only error here that a caller should retry: the fix is to re-read the series
 * and recompute, never to re-send the same number. Thrown rather than returned because the write
 * genuinely did not happen and there is nothing partial to report.
 */
export class VersionCollision extends Error {
  /**
   * @param {string} effectiveFrom - 'YYYY-MM-DD'
   * @param {number} version
   */
  constructor(effectiveFrom, version) {
    super(`A version ${version} effective ${effectiveFrom} already exists.`);
    this.name = 'VersionCollision';
    this.effectiveFrom = effectiveFrom;
    this.version = version;
  }
}

/**
 * The requested effective date is not one this write may take.
 *
 * NOT retryable, and not necessarily a programming error either — the editor bounds the dates it
 * offers, but a timeline read minutes ago goes stale while the user types, so a colleague scheduling a
 * change can move the floor under an open dialog. The caller has to surface this rather than swallow
 * it.
 *
 * BOUNDS AT EITHER END, because the writes that raise it are bounded differently. Appending to an
 * existing series has a floor, the day after the version in force, and no ceiling: scheduling a change
 * forward is the point of a timeline. Starting a funding source has a ceiling of today and no floor,
 * because a payer that applies from a later date applies on no date at all — see
 * `startFundingSource` and `fundingSourceDating` for that rule, and note it is the entity's own:
 * another versioned record may well answer it the other way.
 *
 * ONE ERROR TYPE RATHER THAN TWO, because a caller only ever distinguishes "this date is not allowed"
 * from "try again", and both are already handled together at every catch site.
 */
export class EffectiveDateRejected extends Error {
  /**
   * @param {string} effectiveFrom - 'YYYY-MM-DD'
   * @param {{ earliest?: string|null, latest?: string|null }} [bounds] - 'YYYY-MM-DD' each; null or
   *   absent means unbounded at that end
   */
  constructor(effectiveFrom, { earliest = null, latest = null } = {}) {
    const limits = [
      earliest === null ? null : `no earlier than ${earliest}`,
      latest === null ? null : `no later than ${latest}`,
    ].filter((limit) => limit !== null);

    const bound = limits.length === 0 ? '' : `; it must be ${limits.join(' and ')}`;

    super(`Effective date ${effectiveFrom} is not allowed${bound}.`);
    this.name = 'EffectiveDateRejected';
    this.effectiveFrom = effectiveFrom;
    this.earliest = earliest;
    this.latest = latest;
  }
}
